import artifactPanelsCopy from '@/i18n/ns/artifact-panels';
import { formatCopy, useNamespaceCopy } from '@/i18n/ns';
import { Button } from '@/components/ui/button';
import { Callout } from '@/components/ui/content';
import {
  Menu,
  MenuContent,
  MenuRadioGroup,
  MenuRadioItem,
  MenuTrigger,
} from '@/components/ui/menu';
import { OriginTag, Segmented, StatusPill } from '@/components/ui/kit';
import { EmptyState, LoadingState } from '../primitives';
import { WorkspaceIcon, type WorkspaceIconName } from '../icons';
import { ChevronDownIcon } from 'lucide-react';
import { useEffect, useRef, useState, type ReactNode } from 'react';
import { BottomSheet, SheetRadioList } from '@/components/ui/sheet';
import { useNarrowViewport } from '@/lib/mobile';
import { useIntlLocale } from '../intl-locale';
import { useTimeLabel } from './format';
import { useSessionPicture } from '../apps/session-picture';
import type {
  LibraryItemVM,
  LibraryKind,
  LibraryKindFilter,
  LibrarySegment,
  LibraryVM,
} from './types';

// The Artifact Library list: segments (kit Segmented), search, a menu-backed
// kind filter, and one row per Artifact: the kind's own icon tile, kind and
// publish state, how it exports, the owner or the Agent that made it, and a
// refresh failure with its Try again. The whole row opens the Artifact. Filtering
// and paging are the caller's. Artifacts only; app tiles are a PR 2 addition.

export interface LibraryListProps {
  vm: LibraryVM;
  onSegment?: (segment: LibrarySegment) => void;
  onQuery?: (query: string) => void;
  onKind?: (kind: LibraryKindFilter) => void;
  onOpen?: (id: string) => void;
  onMore?: () => void;
  onRetry?: () => void;
  /** Retry a failed refresh for one Artifact. */
  onRetryRefresh?: (id: string) => void;
  /** Empty state next step: opens the composer. */
  onMake?: () => void;
  /** Inside a page that already titles it (the Library page): the panel's own title stays
   *  for assistive tech but is not drawn, and the panel's side padding is dropped. */
  embedded?: boolean;
  /** The kind filters on offer (PR2 adds 'apps' once the App descriptor is registered). */
  filters?: readonly LibraryKindFilter[];
}

/** The PR1 kinds; the App kind joins the Library only through the registered descriptor (PR2). */
export const LIBRARY_KINDS: LibraryKind[] = [
  'report',
  'page',
  'site',
  'deck',
  'document',
  'spreadsheet',
  'dashboard',
  'table',
  'chart',
  'markdown',
  'kpi',
];
const KIND_KEY = {
  report: ['kindReport', 'kindExportsReport'],
  page: ['kindPage', 'kindExportsPage'],
  site: ['kindSite', 'kindExportsSite'],
  deck: ['kindDeck', 'kindExportsDeck'],
  document: ['kindDocument', 'kindExportsDocument'],
  spreadsheet: ['kindSpreadsheet', 'kindExportsSpreadsheet'],
  dashboard: ['kindDashboard', 'kindExportsDashboard'],
  table: ['kindTable', 'kindExportsTable'],
  chart: ['kindChart', 'kindExportsChart'],
  markdown: ['kindMarkdown', null],
  kpi: ['kindKpi', null],
  app: ['kindApp', null],
} as const;
// The kind's own icon until SHARE supplies real miniatures.
const KIND_ICON: Record<LibraryKind, WorkspaceIconName> = {
  report: 'results',
  page: 'globe',
  site: 'globe',
  deck: 'play',
  document: 'file',
  spreadsheet: 'table',
  dashboard: 'card',
  table: 'table',
  chart: 'results',
  markdown: 'file',
  kpi: 'card',
  app: 'app',
};

/** A deck's first slide, or the kind's icon while it loads or when it can't be read. */
function LibraryThumb({ src: requested, kind }: { src: string | null; kind: LibraryKind }) {
  const { src } = useSessionPicture(requested);
  const [failed, setFailed] = useState<string | null>(null);
  return src && failed !== src ? (
    <img className="ap-thumb" src={src} alt="" loading="lazy" onError={() => setFailed(src)} />
  ) : (
    <span className="ap-thumb" aria-hidden="true">
      <WorkspaceIcon name={KIND_ICON[kind]} size={18} />
    </span>
  );
}

const FILTERS: [LibraryKindFilter, string][] = [
  ['all', 'libraryKindAll'],
  ['reports', 'libraryKindReports'],
  ['decks', 'libraryKindDecks'],
  ['documents', 'libraryKindDocuments'],
  ['spreadsheets', 'libraryKindSpreadsheets'],
  ['apps', 'libraryKindApps'],
];
/** The filters every host offers; Apps joins them only when the host says so (PR2). */
const PR1_FILTERS: readonly LibraryKindFilter[] = [
  'all',
  'reports',
  'decks',
  'documents',
  'spreadsheets',
];

/**
 * The kind filter: the kit Menu on a pointer screen, the kit bottom sheet (titled, with scrim and
 * handle) on a phone. Same values and same one-choice behaviour either way.
 */
function KindFilter({
  value,
  label,
  ariaLabel,
  title,
  options,
  onChange,
}: {
  value: string;
  label: string;
  ariaLabel: string;
  title: string;
  options: { value: string; label: string }[];
  onChange: (value: string) => void;
}) {
  const narrow = useNarrowViewport();
  const [open, setOpen] = useState(false);
  // Crossing the phone breakpoint never carries an open sheet across: closed on every change.
  useEffect(() => setOpen(false), [narrow]);
  const trigger = useRef<HTMLButtonElement>(null);
  const button = (
    <Button
      ref={trigger}
      type="button"
      variant="secondary"
      className="ap-kind-trigger"
      aria-label={ariaLabel}
      aria-haspopup={narrow ? 'dialog' : undefined}
      onClick={narrow ? () => setOpen(true) : undefined}
    >
      {label}
      <ChevronDownIcon aria-hidden="true" />
    </Button>
  );
  if (narrow)
    return (
      <>
        {button}
        <BottomSheet open={open} onOpenChange={setOpen} title={title} returnFocus={trigger}>
          <SheetRadioList
            label={title}
            choices={options}
            value={value}
            onChoose={(next) => {
              setOpen(false);
              if (next !== value) onChange(next);
            }}
          />
        </BottomSheet>
      </>
    );
  return (
    <Menu>
      <MenuTrigger asChild>{button}</MenuTrigger>
      <MenuContent label={ariaLabel}>
        <MenuRadioGroup value={value} onValueChange={onChange}>
          {options.map((option) => (
            <MenuRadioItem key={option.value} value={option.value}>
              {option.label}
            </MenuRadioItem>
          ))}
        </MenuRadioGroup>
      </MenuContent>
    </Menu>
  );
}

export function LibraryList({
  vm,
  onSegment,
  onQuery,
  onKind,
  onOpen,
  onMore,
  onRetry,
  onRetryRefresh,
  onMake,
  embedded = false,
  filters = PR1_FILTERS,
}: LibraryListProps) {
  const copy = useNamespaceCopy(artifactPanelsCopy);
  const filtered = vm.query.trim() !== '' || vm.kind !== 'all';
  const kindLabel =
    copy[(FILTERS.find(([value]) => value === vm.kind) ?? FILTERS[0])[1] as keyof typeof copy];
  return (
    <section
      className="ap-panel"
      aria-labelledby="ap-library-title"
      data-panel="library"
      data-embedded={embedded ? 'true' : undefined}
    >
      <header className={embedded ? 'ap-head ap-visually-hidden' : 'ap-head'}>
        <h2 id="ap-library-title">{copy.libraryTitle}</h2>
      </header>
      <div className="ap-toolbar">
        <Segmented<LibrarySegment>
          label={copy.libraryTitle}
          value={vm.segment}
          onChange={(segment) => onSegment?.(segment)}
          options={[
            { value: 'all', label: copy.librarySegmentAll },
            { value: 'mine', label: copy.librarySegmentMine },
            { value: 'shared', label: copy.librarySegmentShared },
          ]}
        />
        <input
          type="search"
          aria-label={copy.librarySearch}
          placeholder={copy.librarySearch}
          value={vm.query}
          onChange={(event) => onQuery?.(event.currentTarget.value)}
        />
        <KindFilter
          value={vm.kind}
          label={kindLabel}
          ariaLabel={copy.libraryKindAll}
          title={copy.libraryKindSheetTitle}
          options={FILTERS.filter(([value]) => filters.includes(value)).map(([value, key]) => ({
            value,
            label: copy[key as keyof typeof copy],
          }))}
          onChange={(value) => onKind?.(value as LibraryKindFilter)}
        />
      </div>
      {vm.load === 'loading' && vm.items.length === 0 && (
        <LoadingState label={copy.libraryLoading} />
      )}
      {vm.load === 'loading' && vm.items.length > 0 && (
        <div
          className="ws-library-feed-progress"
          role="progressbar"
          aria-busy="true"
          aria-label={copy.libraryLoading}
        />
      )}
      {vm.load === 'error' && (
        <Callout
          tone="info"
          actions={
            <Button type="button" variant="outline" size="sm" onClick={onRetry}>
              {copy.retry}
            </Button>
          }
        >
          {copy.libraryErrorBody}
        </Callout>
      )}
      {vm.load === 'ready' && vm.items.length === 0 && (
        <EmptyState
          icon="files"
          title={
            filtered
              ? copy.libraryNoMatchTitle
              : vm.segment === 'all'
                ? copy.libraryEmptyAllTitle
                : copy.libraryEmptyTitle
          }
          description={
            filtered
              ? copy.libraryNoMatchBody
              : vm.segment === 'mine'
                ? copy.libraryEmptyMineBody
                : vm.segment === 'shared'
                  ? copy.libraryEmptySharedHint
                  : copy.libraryEmptyAllBody
          }
        >
          {!filtered && vm.segment !== 'shared' && onMake && (
            <Button type="button" onClick={onMake}>
              {copy.libraryMake}
            </Button>
          )}
        </EmptyState>
      )}
      {(vm.load === 'ready' || vm.load === 'loading') && vm.items.length > 0 && (
        <ul className="ap-list" aria-busy={vm.load === 'loading' || undefined}>
          {vm.items.map((item) => (
            <LibraryRow key={item.id} item={item} onOpen={onOpen} onRetryRefresh={onRetryRefresh} />
          ))}
        </ul>
      )}
      {vm.load === 'ready' && vm.hasMore && (
        <Button type="button" variant="outline" onClick={onMore}>
          {copy.libraryMore}
        </Button>
      )}
    </section>
  );
}

const FORMAT_KEY = {
  pptx: 'exportFormatPptx',
  pdf: 'exportFormatPdf',
  docx: 'exportFormatDocx',
  xlsx: 'exportFormatXlsx',
  csv: 'exportFormatCsv',
} as const;

/** The Note and Metric lines come from registry facts on the row, never from the kind name:
 *  a Note lists its real export formats (or says it opens to read), "Read-only" only when the
 *  registry says so; a Metric says "Live view" only when live-bound, else when its one number
 *  was taken. Unknown facts say nothing. */
function kindLine(
  copy: Readonly<Record<string, string>>,
  item: LibraryItemVM,
  time: (value: string | null | undefined) => string | null,
  locale: string,
): ReactNode {
  if (item.kind === 'markdown') {
    const names = item.exportFormats.map(
      (id) => copy[FORMAT_KEY[id as keyof typeof FORMAT_KEY]] ?? id.toUpperCase(),
    );
    const base = names.length
      ? formatCopy(copy.libraryExportsList, {
          formats: new Intl.ListFormat(locale, { type: 'conjunction' }).format(names),
        })
      : copy.libraryOpensToRead;
    return item.readOnly === true ? `${base} · ${copy.libraryReadOnly}` : base;
  }
  if (item.kind === 'app') {
    // Only the formats the registered App actually supports; none means no export line at all.
    if (!item.exportFormats.length) return null;
    const names = item.exportFormats.map(
      (id) => copy[FORMAT_KEY[id as keyof typeof FORMAT_KEY]] ?? id.toUpperCase(),
    );
    return formatCopy(copy.libraryExportsList, {
      formats: new Intl.ListFormat(locale, { type: 'conjunction' }).format(names),
    });
  }
  if (item.kind === 'kpi') {
    if (item.liveBound === true)
      return (
        <>
          <span className="ap-live-dot" aria-hidden="true" />
          {copy.libraryLiveView}
        </>
      );
    const when = time(item.valueAsOf);
    return when ? formatCopy(copy.libraryOneNumber, { time: when }) : null;
  }
  return null;
}

function LibraryRow({
  item,
  onOpen,
  onRetryRefresh,
}: {
  item: LibraryItemVM;
  onOpen?: (id: string) => void;
  onRetryRefresh?: (id: string) => void;
}) {
  const copy = useNamespaceCopy(artifactPanelsCopy);
  const time = useTimeLabel();
  const [kindKey, exportsKey] = KIND_KEY[item.kind];
  const line = kindLine(copy, item, time, useIntlLocale());
  const updated = time(item.updatedAt);
  const failedAt = time(item.refreshFailedAt);
  return (
    <li className="ap-library-item">
      <button type="button" className="ap-row ap-library-row" onClick={() => onOpen?.(item.id)}>
        <LibraryThumb src={item.thumbnailUrl} kind={item.kind} />
        <span className="ap-row-copy">
          <strong>
            {item.title}
            <StatusPill>{copy[kindKey]}</StatusPill>
            {item.publish === 'published' && item.publishedNumber !== null && (
              <StatusPill tone="success">
                {formatCopy(copy.libraryPublishedAs, { number: String(item.publishedNumber) })}
              </StatusPill>
            )}
            {item.publish === 'draft' && <StatusPill>{copy.libraryDraft}</StatusPill>}
            {item.publish === 'unpublished' && <StatusPill>{copy.libraryUnpublished}</StatusPill>}
            {item.refresh === 'failed' && (
              <StatusPill tone="warning">{copy.libraryRefreshFailed}</StatusPill>
            )}
            {item.refresh === 'running' && (
              <StatusPill tone="live">{copy.libraryRefreshRunning}</StatusPill>
            )}
          </strong>
          {(exportsKey ? copy[exportsKey] : line) && (
            <span>{exportsKey ? copy[exportsKey] : line}</span>
          )}
          <span>
            {item.madeByAgent ? (
              <OriginTag label={formatCopy(copy.libraryVia, { owner: item.ownerLabel })} />
            ) : (
              formatCopy(copy.libraryOwner, { owner: item.ownerLabel })
            )}
            {updated ? ` · ${formatCopy(copy.libraryUpdated, { time: updated })}` : ''}
          </span>
          {item.audienceLabel && (
            <span>{formatCopy(copy.libraryAudience, { audience: item.audienceLabel })}</span>
          )}
        </span>
        <WorkspaceIcon name="chevron" size={16} />
      </button>
      {item.refresh === 'failed' && (
        <p className="ap-refresh-failed">
          {failedAt
            ? formatCopy(copy.libraryRefreshFailedAt, { time: failedAt })
            : copy.libraryRefreshFailed}{' '}
          {item.canRetryRefresh && onRetryRefresh && (
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => onRetryRefresh?.(item.id)}
            >
              {copy.libraryRefreshTryAgain}
            </Button>
          )}
        </p>
      )}
    </li>
  );
}
